/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import * as CONSTANTS from "../../Constants";
import { CsiDrag, IDragResults } from "../meta/Drag/index";
import { getZoomValue } from "../selectors/index";

interface IAdjustablePointProps {
    /* if true, hide the point.  Used to force updates when dragging */
    hide?: boolean;
    isHandle?: boolean;
    x: number;
    y: number;
    r?: number;
    /** Restrict X from any changes. */
    restrictX?: boolean;
    /** Restrict Y from any changes. */
    restrictY?: boolean;
    fill?: string;
    stroke: string;
    /** Meta data for the point. It will be passed along with events to parent widget. */
    data: any;
    pointerEvents?: string;
    onDrag(key: string | number | undefined, data: any, dx: number, dy: number): void;
    onDragComplete(key: string | number | undefined, data: any, drag: IDragResults): void;
    onPointerDown?(data: any): void;
    onClick?(data: any): void;
    onDblClick?(data: any): void;
}

const DEFAULT_PROPS = {
	fill: "white",
	pointerEvents: "visible",
	r: CONSTANTS.DEFAULT_CONTROL_POINT_RADIUS,
};

/**
 * Adjustable point on the shape. This point can be dragged or clicked. It passes all events to parent widget.
 * Restriction can be set so point can be dragged only on un-restricted axis.
 */
export class AdjustablePoint extends WidgetBase<IAdjustablePointProps> {
	private doubleClickDelay = 100;
	private clickBlocked = false;
	private _dragging = false;

	/**
     * Remove all pointer event listeners on shape
     */
	protected onDetach(): void {
		this.meta(CsiDrag)
			.remove(`AP_${this.properties.key}`);
	}

	/**
     * Mark start of drag as well as pass click event to parent widget.
     *
     * @param event {PointerEvent}
     */
	protected _onPointerDown(event: PointerEvent) {
		this.clickBlocked = false;
		if (this.properties.onPointerDown) {
			this.properties.onPointerDown(this.properties.data);
		}
		setTimeout(() => {
			if (this.clickBlocked) {
				return;
			} else if (this.properties.onClick) {
				this.properties.onClick(this.properties.data);
			}
		}, this.doubleClickDelay);
	}

	protected _onDoubleClick(event: PointerEvent) {
		const { data, onDblClick } = this.properties;
		if (onDblClick) {
			onDblClick(data);
		}
		this.clickBlocked = true;
	}

	/**
     * Handle drag event, pass required information back to parent widget.
     * @param drag {IDragResults} Drag related meta data.
     */
	protected _onPointerDrag(drag: IDragResults) {
		// if dragging is not yet started return
		if (!this._dragging && !drag.isDragging) {
			return;
		}

		if (this._dragging && !drag.isDragging) {
			// if previously dragging was true and now dragResults say it is false then dragging is stopped.
			this._dragging = false;
			if (this.properties.onDragComplete) {
				this.properties.onDragComplete(this.properties.key, this.properties.data, drag);
				return false;
			}
		} else if (!drag.delta.x && !drag.delta.y) {
			return;
		} else if (drag.delta.x !== 0 || drag.delta.y !== 0) {
			this._dragging = true;
		}
		// if there are restrictions on x or y then don't change it's value.
		const dx = this.properties.restrictX ? 0 : drag.delta.x;
		const dy = this.properties.restrictY ? 0 : drag.delta.y;
		if (drag.delta.x !== 0 || drag.delta.y !== 0) {
			this.properties.onDrag(this.properties.key, this.properties.data, dx, dy);
		}
	}

	protected render() {
		const props = { ...DEFAULT_PROPS, ...this.properties };
		const key = `AP_${this.properties.key as string}`;
		const dragResult: IDragResults = this.meta(CsiDrag)
			.get(key);
		this._onPointerDrag(dragResult);

		if (this.properties.isHandle) {
			const size = getZoomValue(props.r * 2);
			const stroke = CONSTANTS.HANDLE_COLOR;
			return (
				<rect
					fill={`${props.fill}`}
					height={`${size}`}
					key={key}
					onpointerdown={this._onPointerDown}
					pointer-events={`${props.pointerEvents}`}
					stroke={`${stroke}`}
					stroke-width={`${getZoomValue(CONSTANTS.SELECTION_HANDLER_STROKE_WIDTH)}`}
					visibility={this.properties.hide ? "hidden" : "visible"}
					width={`${size}`}
					x={`${props.x - size / 2}`}
					y={`${props.y - size / 2}`}
				/>
			);
		} else {
			return (
				<circle
					cx={`${props.x}`}
					cy={`${props.y}`}
					fill={`${props.fill}`}
					key={key}
					ondblclick={this._onDoubleClick}
					onpointerdown={this._onPointerDown}
					pointer-events={`${props.pointerEvents}`}
					r={`${getZoomValue(props.r)}`}
					stroke={`${props.stroke}`}
					stroke-width={`${getZoomValue(CONSTANTS.SELECTION_HANDLER_STROKE_WIDTH)}`}
				/>
			);
		}
	}
}
